/**
 * Schedules React Query hooks
 * Handles fetching, creating, updating, and managing schedules
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNotification } from '@/stores/useNotificationStore';
import * as schedulesApi from '@/services/schedulesApi';
import { STALE_TIME } from '@/utils/constants';
import type { CreateScheduleInput, UpdateScheduleInput } from '@/types/schedule';

/**
 * Query keys for schedules
 */
export const scheduleKeys = {
  all: ['schedules'] as const,
  lists: () => [...scheduleKeys.all, 'list'] as const,
  details: () => [...scheduleKeys.all, 'detail'] as const,
  detail: (id: string) => [...scheduleKeys.details(), id] as const,
  jobs: (id: string) => [...scheduleKeys.detail(id), 'jobs'] as const,
};

/**
 * Hook to fetch all schedules
 */
export function useSchedules() {
  return useQuery({
    queryKey: scheduleKeys.lists(),
    queryFn: () => schedulesApi.getSchedules(),
    staleTime: STALE_TIME.JOBS,
  });
}

/**
 * Hook to fetch a single schedule
 */
export function useSchedule(id: string) {
  return useQuery({
    queryKey: scheduleKeys.detail(id),
    queryFn: () => schedulesApi.getSchedule(id),
    staleTime: STALE_TIME.JOBS,
    enabled: !!id,
  });
}

/**
 * Hook to create a new schedule
 */
export function useCreateSchedule() {
  const queryClient = useQueryClient();
  const { success, error } = useNotification();

  return useMutation({
    mutationFn: (input: CreateScheduleInput) => schedulesApi.createSchedule(input),
    onSuccess: (newSchedule) => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.lists() });
      queryClient.setQueryData(scheduleKeys.detail(newSchedule.id), newSchedule);
      success('Schedule created', `"${newSchedule.name}" will run on schedule`);
    },
    onError: (err) => {
      error('Failed to create schedule', err instanceof Error ? err.message : 'Unknown error');
    },
  });
}

/**
 * Hook to update a schedule
 */
export function useUpdateSchedule() {
  const queryClient = useQueryClient();
  const { success, error } = useNotification();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateScheduleInput }) =>
      schedulesApi.updateSchedule(id, input),
    onSuccess: (updated) => {
      queryClient.setQueryData(scheduleKeys.detail(updated.id), updated);
      queryClient.invalidateQueries({ queryKey: scheduleKeys.lists() });
      success('Schedule updated', `"${updated.name}" has been updated`);
    },
    onError: (err) => {
      error('Failed to update schedule', err instanceof Error ? err.message : 'Unknown error');
    },
  });
}

/**
 * Hook to pause a schedule
 */
export function usePauseSchedule() {
  const queryClient = useQueryClient();
  const { success, error } = useNotification();

  return useMutation({
    mutationFn: (id: string) => schedulesApi.pauseSchedule(id),
    onSuccess: (updated) => {
      queryClient.setQueryData(scheduleKeys.detail(updated.id), updated);
      queryClient.invalidateQueries({ queryKey: scheduleKeys.lists() });
      success('Schedule paused', `"${updated.name}" has been paused`);
    },
    onError: (err) => {
      error('Failed to pause schedule', err instanceof Error ? err.message : 'Unknown error');
    },
  });
}

/**
 * Hook to resume a schedule
 */
export function useResumeSchedule() {
  const queryClient = useQueryClient();
  const { success, error } = useNotification();

  return useMutation({
    mutationFn: (id: string) => schedulesApi.resumeSchedule(id),
    onSuccess: (updated) => {
      queryClient.setQueryData(scheduleKeys.detail(updated.id), updated);
      queryClient.invalidateQueries({ queryKey: scheduleKeys.lists() });
      success('Schedule resumed', `"${updated.name}" has been resumed`);
    },
    onError: (err) => {
      error('Failed to resume schedule', err instanceof Error ? err.message : 'Unknown error');
    },
  });
}

/**
 * Hook to trigger immediate run
 */
export function useTriggerSchedule() {
  const queryClient = useQueryClient();
  const { success, error } = useNotification();

  return useMutation({
    mutationFn: (id: string) => schedulesApi.triggerSchedule(id),
    onSuccess: (updated) => {
      queryClient.setQueryData(scheduleKeys.detail(updated.id), updated);
      queryClient.invalidateQueries({ queryKey: scheduleKeys.lists() });
      success('Schedule triggered', `"${updated.name}" will run now`);
    },
    onError: (err) => {
      error('Failed to trigger schedule', err instanceof Error ? err.message : 'Unknown error');
    },
  });
}

/**
 * Hook to delete a schedule
 */
export function useDeleteSchedule() {
  const queryClient = useQueryClient();
  const { success, error } = useNotification();

  return useMutation({
    mutationFn: (id: string) => schedulesApi.deleteSchedule(id),
    onSuccess: (_, id) => {
      queryClient.removeQueries({ queryKey: scheduleKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: scheduleKeys.lists() });
      success('Schedule deleted', 'The schedule has been deleted');
    },
    onError: (err) => {
      error('Failed to delete schedule', err instanceof Error ? err.message : 'Unknown error');
    },
  });
}

/**
 * Hook to fetch jobs spawned by a schedule
 */
export function useScheduleJobs(id: string) {
  return useQuery({
    queryKey: scheduleKeys.jobs(id),
    queryFn: () => schedulesApi.getScheduleJobs(id),
    staleTime: STALE_TIME.JOBS,
    enabled: !!id,
  });
}
